import {
  ID_TOKEN_KEY,
  ACCESS_TOKEN_KEY,
  ASSO_SUBS,
  REFRESH_TOKEN_KEY,
  SUB_ID,
  IS_SSO_LOGIN_KEY,
  HASURA_CLAIMS_KEY
} from "constants/auth";
import { BASE_URL } from "services/endpoints";
import axios from "axios";
import { decodeToken, getHasuraClaims } from "./jwt-utils";

/**
 * Returns the subscription access token from localStorage.
 */
const getAccessToken = () => {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
};

const getRefreshToken = () => {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
};

const getIDToken = () => {
  return localStorage.getItem(ID_TOKEN_KEY);
};

const getIsSSOLogin = () => {
  return localStorage.getItem(IS_SSO_LOGIN_KEY);
};

const getUser = (): any => {
  const token = getAccessToken();
  if (!token) {
    return null;
  }

  const decoded = decodeToken(token);
  if (!decoded) {
    return null;
  }

  const hasuraClaims = getHasuraClaims(token);

  // replace hasura claims with decoded claims
  decoded[HASURA_CLAIMS_KEY] = hasuraClaims;

  return decoded;
};

const logout = async () => {
  try {
    await axios.post(`${BASE_URL}/logout`, {
      id_token: getIDToken() as string
    });

    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(SUB_ID);
    localStorage.removeItem(ASSO_SUBS);
    localStorage.removeItem(ID_TOKEN_KEY);
    localStorage.removeItem(IS_SSO_LOGIN_KEY);
  } catch (ex) {
    console.error(ex);
  }
  setTimeout(() => {
    window.location.href = "/";
  }, 200);
};

const setIDToken = (token: string) => {
  localStorage.setItem(ID_TOKEN_KEY, token);
};

const setAccessToken = (token: string) => {
  localStorage.setItem(ACCESS_TOKEN_KEY, token);
};

const setRefreshToken = (token: string) => {
  localStorage.setItem(REFRESH_TOKEN_KEY, token);
};

const setIsSSOLogin = (value: string) => {
  localStorage.setItem(IS_SSO_LOGIN_KEY, value);
};

const getAssociatedSubscriptions = (): Array<any> => {
  const associatedSubscriptions = localStorage.getItem(ASSO_SUBS);
  try {
    return JSON.parse(associatedSubscriptions!);
  } catch {
    return [];
  }
};

const setAssociatedSubscriptions = (associatedSubscriptions: Array<any>) => {
  try {
    localStorage.setItem(ASSO_SUBS, JSON.stringify(associatedSubscriptions));
  } catch (err) {
    console.log(err);
  }
};

const getCurrentSubscriptionId = () => {
  return localStorage.getItem(SUB_ID);
};

const setCurrentSubscriptionId = (subscriptionId: string) => {
  localStorage.setItem(SUB_ID, subscriptionId);
};

const setAccessTokenAndSubscriptionId = async () => {
  const params = new URLSearchParams(window.location.search);
  const code = params.get("code") || "";
  const subscriptionId = params.get("subscriptionId") || "";

  if (!code) {
    return;
  }

  try {
    const tokenFetchUrl = `${BASE_URL}/tmp_token/${code}`;
    const tokenFetchResponse = await axios.get(tokenFetchUrl);

    const {
      accessToken,
      id_token: idToken,
      ref_token: refToken
    } = tokenFetchResponse?.data?.success || {};

    if (accessToken) {
      setAccessToken(accessToken);
    }
    if (subscriptionId) {
      setCurrentSubscriptionId(subscriptionId);
    }
    if (idToken) {
      setIDToken(idToken);
    }
    if (refToken) {
      setRefreshToken(refToken);
    }
  } catch (error) {
    console.error("Error fetching tokens:", error);
  }
};

const getLoggedinUserId = (): any => {
  const accessToken = getAccessToken();
  if (!accessToken) {
    return "";
  }
  const hasuraClaims = getHasuraClaims(accessToken);
  const userId = hasuraClaims?.["x-hasura-user-id"];
  return userId || "";
};

const getSubscriptionId = () => {
  try {
    const accessToken = getAccessToken();
    if (!accessToken) {
      return "";
    }
    const hasuraClaims = getHasuraClaims(accessToken);
    const subscriptionId = hasuraClaims?.["x-hasura-subscription-id"];
    return subscriptionId || "";
  } catch (ex) {
    return "";
  }
};

const isTokenExpired = (jwtToken?: string) => {
  if (!jwtToken) {
    return null;
  }

  try {
    const jwt = JSON.parse(atob(jwtToken.split(".")[1]));
    const exp = (jwt && jwt.exp && jwt.exp * 1000) || null;

    if (!exp) {
      return false;
    }
    return Date.now() > exp;
  } catch (error) {
    return "Invalid Token";
  }
};

const AuthUtils = {
  getAccessToken,
  getRefreshToken,
  getIDToken,
  getIsSSOLogin,
  getUser,
  logout,
  setIDToken,
  setAccessToken,
  setRefreshToken,
  setIsSSOLogin,
  getAssociatedSubscriptions,
  setAssociatedSubscriptions,
  getCurrentSubscriptionId,
  setCurrentSubscriptionId,
  setAccessTokenAndSubscriptionId,
  getLoggedinUserId,
  getSubscriptionId,
  isTokenExpired
};

export default AuthUtils;
